import React, { useRef, useEffect, useState } from "react";

const BAR_COUNT = 32;
const BAR_GAP = 3;
const BAR_RADIUS = 3;
const BAR_COLOR = "#00a884";
const BAR_MIN_HEIGHT = 3;
const CANVAS_HEIGHT = 36;
const UPDATE_INTERVAL_MS = 80;

const addZero = (n) => (n < 10 ? "0" + n : n);

const WaveformVisualizer = ({ analyserRef }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [seconds, setSeconds] = useState(0);

  // Timer
  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Canvas waveform loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let lastDraw = 0;
    let dataArray = null;

    const draw = (timestamp) => {
      rafRef.current = requestAnimationFrame(draw);

      if (timestamp - lastDraw < UPDATE_INTERVAL_MS) return;
      lastDraw = timestamp;

      const analyser = analyserRef.current;
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      if (!analyser) {
        // Idle animation while analyser not ready
        drawIdleBars(ctx, width, height, timestamp);
        return;
      }

      if (!dataArray || dataArray.length !== analyser.frequencyBinCount) {
        dataArray = new Uint8Array(analyser.frequencyBinCount);
      }
      analyser.getByteFrequencyData(dataArray);

      const barWidth = (width - BAR_GAP * (BAR_COUNT - 1)) / BAR_COUNT;
      const usableBins = Math.floor(dataArray.length * 0.6); // use lower 60% of spectrum (voice range)
      const step = Math.floor(usableBins / BAR_COUNT);

      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) {
          sum += dataArray[i * step + j] || 0;
        }
        const avg = sum / step;
        const normalized = avg / 255;
        const barHeight = Math.max(BAR_MIN_HEIGHT, normalized * (height - 4));

        const x = i * (barWidth + BAR_GAP);
        const y = (height - barHeight) / 2;

        ctx.fillStyle = BAR_COLOR;
        roundRect(ctx, x, y, barWidth, barHeight, BAR_RADIUS);
        ctx.fill();
      }
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [analyserRef]);

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      flex: 1,
      minWidth: 0,
      padding: "0 4px",
    }}>
      {/* Punto rojo pulsante */}
      <span style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "#f44336",
        flexShrink: 0,
        animation: "waveformPulse 1s ease-in-out infinite",
      }} />

      {/* Timer */}
      <span style={{
        fontSize: 13,
        fontVariantNumeric: "tabular-nums",
        color: "#555",
        flexShrink: 0,
        minWidth: 36,
      }}>
        {`${addZero(minutes)}:${addZero(secs)}`}
      </span>

      {/* Canvas waveform */}
      <canvas
        ref={canvasRef}
        width={180}
        height={CANVAS_HEIGHT}
        style={{ flex: 1, maxWidth: 200, display: "block" }}
      />

      <style>{`
        @keyframes waveformPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

function drawIdleBars(ctx, width, height, timestamp) {
  const barWidth = (width - BAR_GAP * (BAR_COUNT - 1)) / BAR_COUNT;
  for (let i = 0; i < BAR_COUNT; i++) {
    const phase = (timestamp / 600 + i * 0.3) % (Math.PI * 2);
    const barHeight = Math.max(BAR_MIN_HEIGHT, 4 + Math.sin(phase) * 3);
    const x = i * (barWidth + BAR_GAP);
    const y = (height - barHeight) / 2;
    ctx.fillStyle = "#ccc";
    roundRect(ctx, x, y, barWidth, barHeight, BAR_RADIUS);
    ctx.fill();
  }
}

function roundRect(ctx, x, y, w, h, r) {
  if (h < r * 2) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default WaveformVisualizer;
